import { useCallback, useEffect, useState, useMemo } from 'react';
import { useFilteredViewContextInput } from '@atlassian/jira-business-filters/src/controllers/default-query-input/index.tsx';
import { useSimpleSearchJQL } from '@atlassian/jira-business-filters/src/controllers/simple-search/index.tsx';
import { useJqlPagination } from '@atlassian/jira-business-list-handlers/src/controllers/pagination/main.tsx';
import { getAGGCompliantData } from '@atlassian/jira-business-list-handlers/src/controllers/pagination/utils.tsx';
import { REQUEST_END, REQUEST_START } from '@atlassian/jira-business-performance/src/constants.tsx';
import { markViewMetric } from '@atlassian/jira-business-performance/src/ui/page-load/utils.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useStartDateField } from '@atlassian/jira-router-resources-business-configuration/src/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { transformData } from '../../common/utils/transform-data/index.tsx';
import type { CalendarDataSourceItem, CalendarIssue } from '../../types.tsx';
import { CALENDAR_ITEM_FIELDS, CALENDAR_ITEM_PAGE_SIZE } from '../constants.tsx';
import type {
	JWMCalendarPaginatedItems,
	JWMCalendarPaginatedItemsVariables,
	JWMCalendarPaginatedItems_jira_list_JiraWorkManagementViewItemConnection_edges_node as ViewItemsNode,
	JWMCalendarPaginatedItems_jira_list_JiraWorkManagementViewItemConnection_edges as ViewItemsEdges,
} from './__generated_apollo__/JWMCalendarPaginatedItems';
import { QUERY } from './gql.tsx';
import type { UseCalendarItemsRequest, UseCalendarIssuesReturn } from './types.tsx';
import { toQuery } from './utils.tsx';

const QUERY_NAME = 'calendarViewItemsQuery';

/**
 * This is a local cache.
 * During refetch calls, with pagination, we end up re-loading all our items. If calendar has more than 100 items,
 * there was flickering while rendering because items were loaded and sorted based on start date for every paginated call.
 * This cache is only busted when all items are loaded drastically improving the rendering of calendar view with more than 100 items.
 */
let allCalendarItems: CalendarDataSourceItem<CalendarIssue>[] | null = [];

export const useCalendarIssues = (request: UseCalendarItemsRequest): UseCalendarIssuesReturn => {
	const { data: startDateField, loading: startDateFieldLoading } = useStartDateField();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { quickFiltersJql } = useFilteredViewContextInput({ view: 'calendar' });
	const { isSearching } = useSimpleSearchJQL();
	const [hasLoadingStarted, setHasLoadingStarted] = useState(false);
	const cloudId = useCloudId();
	const defaultVariables = useMemo(
		() => ({
			cloudId,
			first: CALENDAR_ITEM_PAGE_SIZE,
			fieldIds: CALENDAR_ITEM_FIELDS,
		}),
		[cloudId],
	);

	let startDateFieldId = null;
	if (startDateField?.id) {
		[, startDateFieldId] = startDateField.id.split('_');
	}

	const { loading, error, data, loadFirstPage, isLoadingNextPage, loadNextPage, refetch } =
		useJqlPagination<JWMCalendarPaginatedItems, ViewItemsNode, JWMCalendarPaginatedItemsVariables>({
			variables: {
				...defaultVariables,
				jql: toQuery(request, quickFiltersJql, startDateFieldId),
			},
			query: QUERY,
			fetchPolicy: 'cache-and-network',
			onError: (apolloError) => {
				fireErrorAnalytics({
					meta: {
						id: QUERY_NAME,
						packageName: 'jiraBusinessCalendar',
						teamName: 'jira-yurree',
					},
					attributes: {
						message: 'Failed to fetch issues for calendar view',
						isPaginationEnabled: true,
					},
					error: apolloError,
					sendToPrivacyUnsafeSplunk: true,
				});
				markViewMetric('calendar', REQUEST_END);
			},
			onCompleted: (responseData) => {
				fireOperationalAnalytics(createAnalyticsEvent({}), `${QUERY_NAME} succeeded`, {
					issueCount: getAGGCompliantData<JWMCalendarPaginatedItems>(responseData, 'list')
						?.totalCount,
					isPaginationEnabled: true,
				});

				if (isSearching) {
					fireOperationalAnalytics(createAnalyticsEvent({}), 'calendarSimpleSearchQuery succeeded');
				}
				markViewMetric('calendar', REQUEST_END);
			},
			onPaginationError: (apolloError) => {
				fireErrorAnalytics({
					meta: {
						id: QUERY_NAME,
						packageName: 'jiraBusinessCalendar',
						teamName: 'jira-yurree',
					},
					attributes: {
						message: 'Pagination failed to fetch issues for calendar view',
					},
					error: apolloError,
					sendToPrivacyUnsafeSplunk: true,
				});
			},
		});

	useEffect(() => {
		if (startDateFieldLoading) {
			return;
		}
		markViewMetric('calendar', REQUEST_START);
		loadFirstPage(); // fetch issues once on mount
		setHasLoadingStarted(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [startDateFieldLoading]);

	const _data = getAGGCompliantData<JWMCalendarPaginatedItems>(data, 'list');
	const hasNextPage = _data?.pageInfo?.hasNextPage ?? false;

	useEffect(() => {
		if (!isLoadingNextPage && hasNextPage) {
			loadNextPage();
			fireOperationalAnalytics(createAnalyticsEvent({}), `${QUERY_NAME} loadNextPage`, {
				issueCount: _data?.totalCount,
				isPaginationEnabled: true,
			});
		}
	}, [isLoadingNextPage, hasNextPage, loadNextPage, createAnalyticsEvent, _data?.totalCount]);

	useMemo(() => {
		if (!loading && !isLoadingNextPage && !hasNextPage) {
			allCalendarItems = transformData<ViewItemsEdges, CalendarDataSourceItem<CalendarIssue>>({
				// @ts-expect-error - TS2739 - Type 'readonly (ListConnectionEdge<object> | null)[]' is missing the following properties from type 'JWMCalendarPaginatedItems_jira_list_JiraWorkManagementViewItemConnection_edges': __typename, nodets
				edges: _data?.edges || [],
				areUnscheduledItems: false,
			});
		}
	}, [hasNextPage, isLoadingNextPage, loading, _data]);

	const refetchFn = useCallback(
		async (refetchRequest?: UseCalendarItemsRequest) => {
			if (fg('business-calendar-refetch-before-available-fix')) {
				// refetch is only available in useLazyQuery after it has been run at least once
				refetchRequest
					? await refetch?.({ ...defaultVariables, jql: toQuery(refetchRequest) })
					: await refetch?.();
			} else {
				if (refetchRequest === undefined) {
					await refetch();
					return;
				}
				await refetch({ ...defaultVariables, jql: toQuery(refetchRequest) });
			}
		},
		[defaultVariables, refetch],
	);

	return {
		// Make sure to return loading=true until items are loaded.
		loading: hasLoadingStarted ? loading || isLoadingNextPage : true,
		error,
		refetch: refetchFn,
		data: allCalendarItems,
	};
};
